import {
  Flex,
  IconButton,
  IconCheckLine,
  IconEditLine,
  IconXLine,
  ScreenReaderContent,
  Spinner,
  Text,
  Tooltip,
} from "@instructure/ui";
import { t } from "i18next";
import { useContext, useEffect, useState } from "react";
import { LearningOutcomesService } from "../../../../services/LearningOutcomesService";
import {
  ERROR,
  useNotification,
} from "../../../CustomHooks/NotificationProvider";
import { MULTI, Selector } from "../../../Selectors/Selector";
import { OutcomesContext } from "../Review";

const OutcomeMastery = ({ outcome, archived }) => {
  const showNotification = useNotification();
  const { taxonomyLevels } = useContext(OutcomesContext);
  const [editDemonstrationLevels, setEditDemonstrationLevels] = useState(false);
  const [initialLevels, setInitialLevels] = useState(null);
  const [demonstratedLevels, setDemonstratedLevels] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let levels = [];
    outcome.demonstrated_skill_levels.forEach((level) => {
      level.skill_level_demonstrated &&
        level.skill_level_demonstrated.verb &&
        levels.push({
          ...level.skill_level_demonstrated,
          title: level.skill_level_demonstrated.verb,
        });
    });
    setDemonstratedLevels(levels);
  }, [outcome]);

  const saveOutcomeLevel = (id, levels) => {
    if (!saving) {
      setSaving(true);
      LearningOutcomesService.setTaxonomyLevelToOutcome(id, levels)
        .then(() => setEditDemonstrationLevels(false))
        .catch(() => {
          showNotification(t("notifications.analyze.taxonomySetError"), ERROR);
        })
        .finally(() => setSaving(false));
    }
  };

  return (
    <Flex gap="xx-small">
      <Flex.Item>
        <Text>{t("analyze.demonstrateMastery")}</Text>
      </Flex.Item>
      {!editDemonstrationLevels && (
        <Flex.Item>
          {demonstratedLevels.length === 0
            ? t("global.none")
            : demonstratedLevels.map((level, key) => (
                <span key={key} style={{ fontWeight: "bold" }}>
                  {level.title}
                  {demonstratedLevels.length - 1 > key ? ", " : ""}
                </span>
              ))}
        </Flex.Item>
      )}
      {!outcome.unapproved && !editDemonstrationLevels && !archived && (
        <Flex.Item>
          <Tooltip
            renderTip={t("analyze.editLearningOutcomeLevelScreenReaderLabel")}
            color="primary-inverse"
          >
            <IconButton
              screenReaderLabel={t(
                "analyze.editLearningOutcomeLevelScreenReaderLabel"
              )}
              size="small"
              onClick={() => {
                setInitialLevels(demonstratedLevels);
                setEditDemonstrationLevels(true);
              }}
              renderIcon={IconEditLine}
            />
          </Tooltip>
        </Flex.Item>
      )}
      {editDemonstrationLevels && (
        <Flex.Item shouldShrink>
          <label htmlFor="slo-skill-level-demonstrated">
            <ScreenReaderContent>
              {t("analyze.taxonomy.levelTitle")}
            </ScreenReaderContent>
          </label>
          <Selector
            size="small"
            type={MULTI}
            id="slo-skill-level-demonstrated"
            options={taxonomyLevels}
            selectedOptions={demonstratedLevels.map((level) => level.id)}
            selectHandler={(selection) =>
              setDemonstratedLevels(
                taxonomyLevels.filter((level) => selection.includes(level.id))
              )
            }
          />
        </Flex.Item>
      )}
      {editDemonstrationLevels && (
        <Flex.Item>
          <Tooltip
            renderTip={t("analyze.cancelEditLevelScreenReaderLabel")}
            color="primary-inverse"
          >
            <IconButton
              screenReaderLabel={t("analyze.cancelEditLevelScreenReaderLabel")}
              size="small"
              onClick={() => {
                setDemonstratedLevels(initialLevels);
                setInitialLevels(null);
                setEditDemonstrationLevels(false);
              }}
              renderIcon={IconXLine}
            />
          </Tooltip>
        </Flex.Item>
      )}
      {editDemonstrationLevels && (
        <Flex.Item>
          <Tooltip
            renderTip={t("analyze.saveLearningOutcomeLevelScreenReaderLabel")}
            color="primary-inverse"
          >
            <IconButton
              screenReaderLabel={t(
                "analyze.saveLearningOutcomeLevelScreenReaderLabel"
              )}
              size="small"
              onClick={() =>
                saveOutcomeLevel(
                  outcome.id,
                  demonstratedLevels.map((lvl) => lvl.id)
                )
              }
              renderIcon={
                saving ? (
                  <Spinner
                    renderTitle={t("global.pleaseWait")}
                    size="x-small"
                  />
                ) : (
                  IconCheckLine
                )
              }
              disabled={saving}
            />
          </Tooltip>
        </Flex.Item>
      )}
    </Flex>
  );
};

export default OutcomeMastery;
